import { Pagination } from "services/search";

import {
  FleetFireExtinguisherRow,
  FleetFireExtinguisherSummary,
  FleetVehicleFireExtinguisher,
  FleetVehicleFireExtinguisherEditFormValues,
  FleetVehicleFireExtinguisherRow,
} from "./types";

import { DataWithPagination } from "services/api/types";

import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import {
  transformFleetFireExtinguisherRow,
  transformFleetVehicleFireExtinguisher,
  transformFleetVehicleFireExtinguisherOnCreate,
  transformFleetVehicleFireExtinguisherOnUpdate,
  transformFleetVehicleFireExtinguisherRow,
} from "./transformations";
import { transformErrorResponse } from "services/api/transformation";

/*---------------------------------------------------------------------------------------
  SEARCH
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleFireExtinguishersAPI = async (
  vehicleId: string,
  pagination: Pagination,
  name?: string | null
): Promise<DataWithPagination<FleetVehicleFireExtinguisherRow>> => {
  let path = `/v1/fleet/fire-extinguishers?vehicleId=${vehicleId}`;

  if(name) path += `&name=${name}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleFireExtinguisherRow
  );
};

/*---------------------------------------------------------------------------------------
  SEARCH — GLOBALNO (kroz sva vozila)
-----------------------------------------------------------------------------------------*/
export const getFleetFireExtinguishersAPI = async (
  pagination: Pagination,
  name?: string | null,
  expirationDateFrom?: Date | null,
  expirationDateTo?: Date | null,
  status?: string | null,
  vehicleManufacturerId?: string | null,
  vehicleModelId?: string | null,
  vehicleTypeId?: string | null
): Promise<DataWithPagination<FleetFireExtinguisherRow>> => {
  let path = `/v1/fleet/fire-extinguishers?name=`;

  if (name) path += `${name}`;
  if (expirationDateFrom)
    path += `&expirationDateFrom=${expirationDateFrom.toISOString()}`;
  if (expirationDateTo)
    path += `&expirationDateTo=${expirationDateTo.toISOString()}`;
  if (status) path += `&status=${status}`;
  if (vehicleManufacturerId) path += `&vehicleManufacturerId=${vehicleManufacturerId}`;
  if (vehicleModelId) path += `&vehicleModelId=${vehicleModelId}`;
  if (vehicleTypeId) path += `&vehicleTypeId=${vehicleTypeId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetFireExtinguisherRow
  );
};

export const getFleetFireExtinguisherSummaryAPI =
  async (): Promise<FleetFireExtinguisherSummary> => {
    const response = await createApiCall(
      {
        path: `/v1/fleet/fire-extinguishers/summary`,
        method: "GET",
      },
      {
        getMessage: {
          error: (error: any) => `${transformErrorResponse(error)}`,
        },
      }
    )();

    return {
      expired: response.data?.expired ?? 0,
      expiring: response.data?.expiring ?? 0,
      valid: response.data?.valid ?? 0,
      total: response.data?.total ?? 0,
    };
  };
/*---------------------------------------------------------------------------------------
  INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleFireExtinguisherAPI = async (
  id: string
): Promise<FleetVehicleFireExtinguisher> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/fire-extinguishers/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleFireExtinguisher(response.data);
};

/*---------------------------------------------------------------------------------------
  CREATE
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleFireExtinguisherAPI = async (
  vehicleId: string,
  formValues: FleetVehicleFireExtinguisherEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleFireExtinguisher> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/fire-extinguishers/",
      method: "POST",
      body: transformFleetVehicleFireExtinguisherOnCreate(formValues, vehicleId),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.fireextinguisher.message.create"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleFireExtinguisher(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleFireExtinguisherAPI = async (
  vehicleId: string,
  id: string,
  formValues: FleetVehicleFireExtinguisherEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleFireExtinguisher> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/fire-extinguishers/${id}`,
      method: "PUT",
      body: transformFleetVehicleFireExtinguisherOnUpdate(formValues, vehicleId),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.fireextinguisher.message.update"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleFireExtinguisher(response.data);
};



/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleFireExtinguisherAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetVehicleFireExtinguisher> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/fire-extinguishers/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.fireextinguisher.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};